/**
 * Class representing an Event Tracker for analytics.
 */
export class EventTracker {
  /**
   * @typedef {object} Tracker
   * @property {(payload: Record<string, string | number | boolean>) => void} recordEvent - Records a general event.
   * @property {(payload: Record<string, string | number | boolean>) => void} recordClick - Records a click event.
   * @property {(payload: Record<string, string | number | boolean>) => void} recordImpression - Records an impression event.
   * @property {(payload: Record<string, string | number | boolean>) => void} recordError - Records an error event.
   */

  /**
   * Function to get the analytics instance.
   * @private
   * @type {() => Tracker | null}
   */
  _getAnalyticsInstance

  /**
   * Creates an instance of EventTracker.
   * @param {() => Tracker | null} getAnalyticsInstance - A function that returns the analytics instance.
   */
  constructor(getAnalyticsInstance = () => window.PAYPAL?.analytics?.instance) {
    this._getAnalyticsInstance = getAnalyticsInstance
  }

  /**
   * Gets the PayPal analytics instance.
   * @private
   * @returns {Tracker | null} The PayPal analytics instance or null if not available.
   */
  _getInstance() {
    return this._getAnalyticsInstance()
  }

  /**
   * Checks if the analytics instance is available.
   * @returns {boolean} True if the instance is available, false otherwise.
   */
  isAvailable() {
    return Boolean(this._getInstance())
  }

  /**
   * Records an event with the analytics instance.
   * @param {Record<string, string | number | boolean>} event - The event data to record.
   */
  recordEvent(event) {
    if (this.isAvailable()) {
      this._getInstance().recordEvent({ data: event })
    }
  }

  /**
   * Records a click event with the analytics instance.
   * @param {Record<string, string | number | boolean>} event - The click event data to record.
   */
  recordClick(event) {
    if (this.isAvailable()) {
      this._getInstance().recordClick({ data: event })
    }
  }

  /**
   * Records an impression event with the analytics instance.
   * @param {Record<string, string | number | boolean>} event - The impression event data to record.
   */
  recordImpression(event) {
    if (this.isAvailable()) {
      this._getInstance().recordImpression({ data: event })
    }
  }

  /**
   * Records an error event with the analytics instance.
   * @param {Record<string, string | number | boolean>} event - The error event data to record.
   */
  recordError(event) {
    if (this.isAvailable()) {
      this._getInstance().recordError({ data: event })
    }
  }
}
